/* eslint-disable @typescript-eslint/no-explicit-any */
import { Entity, FormMode, GenericFieldValue, ModalForm } from '@msdk/definitions'

export interface ModalArgs {
  preventClickOutside?: boolean
  preventEscapeFromClosing?: boolean
}

export interface FormModalArgs extends ModalArgs {
  mode?: FormMode
  modalForm?: ModalForm
  prefilledFields?: Record<string, GenericFieldValue>
  containerEntity?: Entity
}

export interface ViewModalArgs extends ModalArgs {
  lookup?: boolean
  multiSelect?: boolean
  selectedRows?: string[]
  prefilledFilters?: Record<string, GenericFieldValue>
}

export interface FormModal {
  createModal: (
    entityName: string,
    formName: string,
    id: string,
    onDimiss: (args?: any) => void | Promise<void>,
    args?: FormModalArgs,
  ) => ModalRef
  getModal: (entityName: string, formName: string, id: string) => ModalRef
}

export interface ViewModal {
  createModal: (
    entityName: string,
    viewName: string,
    onDimiss: (args?: any) => void | Promise<void>,
    args?: ViewModalArgs,
  ) => ModalRef
  getModal: (entityName: string, viewName: string) => ModalRef
}

export interface ComponentModal {
  createModal: (body: JSX.Element, onDimiss: (args?: any) => void | Promise<void>, args?: ModalArgs) => ModalRef
  getModal: (id: string) => ModalRef
}

export interface CustomSize {
  width?: string
  height?: string
}

export interface DialogModal {
  createModal: (
    title: string,
    message: string,
    buttons: DialogButtons,
    customButtons?: DialogCustomButton[],
    customSize?: CustomSize,
  ) => Promise<DialogResults>
}

export interface DialogInputModal {
  createModal: (
    title: string,
    message: string,
    buttons?: DialogButtons,
    customButtons?: DialogCustomButton[],
    mandatoryInput?: boolean,
  ) => Promise<string | undefined>
}

export enum DialogButtons {
  Yes = 10,
  YesNo = 12,
  Ok = 20,
  OkCancel = 22,
  Custom = 30,
}

export enum DialogResults {
  Yes = 10,
  No = 11,
  Ok = 20,
  Cancel = 21,
}

export enum DialogCustomButtonType {
  Primary = 10,
  Secondary = 20,
  Alternative = 30,
}

export interface DialogCustomButton {
  label: string
  result: DialogResults
  type: DialogCustomButtonType
}

export type ModalResult = any

export class ModalRef {
  public id: string
  public component?: React.ReactNode
  public key: string

  public get status(): ModalStatus {
    return this._status
  }

  private _status: ModalStatus
  private _onClose: (args?: ModalResult) => void | Promise<void>

  constructor(id: string, onClose: () => void | Promise<void>) {
    this.id = id
    this._status = 'Closed'
    this._onClose = onClose
    this.key = crypto.randomUUID()
  }

  public open(): void {
    this._status = 'Open'
  }

  public close(args?: ModalResult): void | Promise<void> {
    this._status = 'Closed'
    this._onClose(args)
  }

  public setStatus(status: ModalStatus) {
    this._status = status
  }
}

type ModalStatus = 'Open' | 'Closed'
